import React, { useState, useEffect, useCallback } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '', style = {} }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={style}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    check: 'M20 6L9 17l-5-5',
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2', 'M10 11v6', 'M14 11v6'],
    power: 'M18.36 6.64a9 9 0 11-12.73 0 M12 2v10',
    calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z M16 2v4 M8 2v4 M3 10h18',
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'],
    more: ['M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0', 'M12 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0', 'M12 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
    image: ['M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z']
};

const defaultForm = {
    operatorId: '',
    planId: '',
    amount: '',
    transactionType: 'Offline',
    startDate: new Date().toISOString().split('T')[0],
    nextBillingDate: '',
};

export default function ManageSubscriptions({ user, onCreate }) {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [activeMenu, setActiveMenu] = useState(null);
    const [editId, setEditId] = useState(null);

    const [form, setForm] = useState(defaultForm);
    const [operators, setOperators] = useState([]);
    const [plans, setPlans] = useState([]);
    const [saving, setSaving] = useState(false);
    const [existingSubs, setExistingSubs] = useState([]);

    useEffect(() => {
        fetchSubscriptions();
        fetchModalData();
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClick = () => setActiveMenu(null);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    const fetchSubscriptions = async () => {
        try {
            const res = await fetch('/api/subscriptions');
            const data = await res.json();
            if (Array.isArray(data)) {
                setSubscriptions(data);
            } else {
                setSubscriptions([]);
            }
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
            showToast('Failed to fetch subscriptions', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchModalData = async () => {
        try {
            const [opsRes, plansRes] = await Promise.all([
                fetch('/api/operators'),
                fetch('/api/price-plans')
            ]);
            const ops = await opsRes.json();
            const pls = await plansRes.json();
            setOperators(Array.isArray(ops) ? ops : []);
            setPlans(Array.isArray(pls) ? pls : []);
        } catch (err) {
            console.error('Error fetching modal data:', err);
        }
    };

    const calculateNextBilling = useCallback((planId, startDate) => {
        const plan = plans.find(p => p._id === planId);
        if (!plan || !plan.pricePeriod) return '';

        const [y, m, d] = startDate.split('-').map(Number);
        const date = new Date(y, m - 1, d);

        const periodStr = plan.pricePeriod.toLowerCase();
        const matches = periodStr.match(/\d+/);
        const val = matches ? parseInt(matches[0]) : null;

        if (periodStr.includes('month') || periodStr.includes('mon')) {
            date.setMonth(date.getMonth() + (val || 1));
        } else if (periodStr.includes('year') || periodStr.includes('yr') || periodStr.includes('annua')) {
            date.setFullYear(date.getFullYear() + (val || 1));
        } else if (periodStr.includes('week') || periodStr.includes('wk')) {
            date.setDate(date.getDate() + ((val || 1) * 7));
        } else if (val !== null) {
            date.setDate(date.getDate() + val);
        } else {
            date.setDate(date.getDate() + 30);
        }

        const resY = date.getFullYear();
        const resM = String(date.getMonth() + 1).padStart(2, '0');
        const resD = String(date.getDate()).padStart(2, '0');
        return `${resY}-${resM}-${resD}`;
    }, [plans]);

    useEffect(() => {
        if (form.planId && form.startDate) {
            const nextDate = calculateNextBilling(form.planId, form.startDate);
            setForm(f => ({ ...f, nextBillingDate: nextDate }));
        }
    }, [form.planId, form.startDate, calculateNextBilling]);

    useEffect(() => {
        if (form.operatorId && showModal) {
            fetchExistingSubscriptions(form.operatorId);
        } else {
            setExistingSubs([]);
        }
    }, [form.operatorId, showModal]);

    const fetchExistingSubscriptions = async (operatorId) => {
        try {
            const res = await fetch(`/api/subscriptions/operator/${operatorId}`);
            if (res.ok) {
                const data = await res.json();
                setExistingSubs(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching existing subscriptions:', err);
        }
    };

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const toggleStatus = async (sub) => {
        const newStatus = sub.status === 'Active' ? 'Inactive' : 'Active';
        try {
            const res = await fetch(`/api/subscriptions/${sub._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) {
                setSubscriptions(prev => prev.map(s => s._id === sub._id ? { ...s, status: newStatus } : s));
                showToast(`Subscription set to ${newStatus}`);
            }
        } catch {
            showToast('Failed to update status', 'error');
        }
    };

    const deleteSubscription = async (id) => {
        if (!window.confirm('Are you sure you want to delete this subscription?')) return;
        try {
            const res = await fetch(`/api/subscriptions/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setSubscriptions(prev => prev.filter(s => s._id !== id));
                showToast('Subscription deleted');
            }
        } catch {
            showToast('Failed to delete', 'error');
        }
    };

    const handleEdit = (sub) => {
        setEditId(sub._id);
        setForm({
            operatorId: sub.operatorId?._id || '',
            planId: sub.planId?._id || '',
            amount: sub.amount || sub.planId?.price || '',
            transactionType: sub.transactionType || 'Offline',
            startDate: new Date(sub.startDate).toISOString().split('T')[0],
            nextBillingDate: sub.nextBillingDate ? new Date(sub.nextBillingDate).toISOString().split('T')[0] : '',
        });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.operatorId || !form.planId || !form.transactionType) {
            showToast('All fields are required', 'error');
            return;
        }
        setSaving(true);
        try {
            const url = editId ? `/api/subscriptions/${editId}` : '/api/subscriptions';
            const method = editId ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    amount: form.amount === '' ? 0 : Number(form.amount)
                }),
            });
            if (res.ok) {
                showToast(`Subscription ${editId ? 'updated' : 'created'} successfully!`);
                setShowModal(false);
                setForm(defaultForm);
                setEditId(null);
                fetchSubscriptions();
            } else {
                const data = await res.json();
                showToast(data.message || 'Failed to save', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        } finally {
            setSaving(false);
        }
    };

    const getTimeRemaining = (date) => {
        if (!date) return 'N/A';
        const now = new Date();
        const future = new Date(date);
        const diffTime = future - now;
        if (diffTime < 0) return 'Overdue';

        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const months = Math.floor(diffDays / 30);
        const remainingDays = diffDays % 30;

        let parts = [];
        if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
        if (remainingDays > 0) parts.push(`${remainingDays} day${remainingDays > 1 ? 's' : ''}`);

        return parts.join(' ') || 'Ends today';
    };

    const filteredSubs = subscriptions.filter(s =>
        (s.planId?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.operatorId?.companyName || '').toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <div className="sub-redesign-container">Loading...</div>;

    return (
        <div className="sub-redesign-container">
            {toast && <div className={`pp-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="sub-header">
                <h1 className="sub-title">Subscription</h1>
                {(user?.userType === 'admin' || user?.permissions?.includes('Create Subscription')) && (
                    <button className="sub-btn-add" onClick={() => { setEditId(null); setForm(defaultForm); setShowModal(true); }}>
                        <Icon d={icons.plus} size={14} style={{ marginRight: 8 }} strokeWidth={3} />
                        Add Subscription
                    </button>
                )}
            </div>

            <div className="sub-card">
                <div className="sub-card-top">
                    <div className="sub-per-page">
                        <select value={perPage} onChange={e => setPerPage(e.target.value)}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                        <span>Subscription Per Page</span>
                    </div>
                    <div className="sub-search">
                        <input
                            type="text"
                            placeholder="Search"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <Icon d={icons.search} size={16} className="search-icon" color="#94a3b8" />
                    </div>
                </div>

                <div className="sub-table-wrapper">
                    <table className="sub-table-new">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>S.No</th>
                                <th>Plan Name</th>
                                <th>Date</th>
                                <th>Next Billing</th>
                                <th>Payment Method</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSubs.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '60px' }}>
                                        No Subscriptions found.
                                    </td>
                                </tr>
                            ) : (
                                filteredSubs.slice(0, perPage).map((sub, idx) => (
                                    <tr key={sub._id}>
                                        <td>{idx + 1}</td>
                                        <td>
                                            <div className="plan-cell">
                                                <div className="plan-icon">
                                                    <Icon d={icons.image} size={16} color="#94a3b8" />
                                                </div>
                                                <div className="plan-info">
                                                    <div className="plan-name">{sub.planId?.name || 'N/A'}</div>
                                                    <div className="plan-sub">Subscribed by {sub.operatorId?.companyName || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="date-cell">
                                                <div>{new Date(sub.startDate).toISOString().split('T')[0]}</div>
                                                <div className="subscribed-on">Subscribed On</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="billing-cell">
                                                <div className="price-label">Price: ${sub.planId?.price?.toLocaleString() || 0}</div>
                                                <div className="time-remaining">{getTimeRemaining(sub.nextBillingDate)}</div>
                                            </div>
                                        </td>
                                        <td className="payment-method">{sub.transactionType || 'Offline'}</td>
                                        <td>
                                            <span className={`status-pill ${sub.status.toLowerCase()}`}>
                                                {sub.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-cell">
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Edit Subscription')) && (
                                                    <button
                                                        className={`action-pill ${sub.status === 'Active' ? 'inactive-btn' : 'active-btn'}`}
                                                        onClick={() => toggleStatus(sub)}
                                                    >
                                                        {sub.status === 'Active' ? 'Inactive' : 'Active'}
                                                    </button>
                                                )}
                                                <div className="more-menu-wrapper" style={{ position: 'relative' }}>
                                                    <button
                                                        className="more-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveMenu(activeMenu === sub._id ? null : sub._id);
                                                        }}
                                                    >
                                                        <Icon d={icons.more} size={16} color="#94a3b8" />
                                                    </button>
                                                    {activeMenu === sub._id && (
                                                        <div className="action-dropdown shadow-lg">
                                                            {(user?.userType === 'admin' || user?.permissions?.includes('Edit Subscription')) && (
                                                                <button onClick={() => handleEdit(sub)}>
                                                                    Edit
                                                                </button>
                                                            )}
                                                            {(user?.userType === 'admin' || user?.permissions?.includes('Delete Subscription')) && (
                                                                <button className="delete" onClick={() => deleteSubscription(sub._id)}>
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="sub-pagination">
                    <div className="pagination-info">
                        Showing 1 to {Math.min(filteredSubs.length, perPage)} of {filteredSubs.length} entries
                    </div>
                    <div className="pagination-ctrls">
                        <button className="page-btn">Previous</button>
                        <button className="page-btn active">1</button>
                        <button className="page-btn">Next</button>
                    </div>
                </div>
            </div>

            {/* Redesigned Modal */}
            {showModal && (
                <div className="sub-modal-overlay">
                    <div className="sub-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editId ? 'Edit Subscription' : 'Add New Subscription'}</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <Icon d={icons.x} size={20} color="#64748b" />
                            </button>
                        </div>
                        <form onSubmit={handleSave} className="modal-form">
                            <div className="form-group">
                                <label>Client<span className="required">*</span></label>
                                <select
                                    value={form.operatorId}
                                    name="operatorId"
                                    onChange={(e) => setForm({ ...form, operatorId: e.target.value })}
                                    required
                                >
                                    <option value="">Select Client</option>
                                    {operators.map(op => <option key={op._id} value={op._id}>{op.companyName}</option>)}
                                </select>
                                {existingSubs.length > 0 && (
                                    <div className="existing-plans-info">
                                        Already Subscribed to: {existingSubs.map(s => s.planId?.name).join(', ')}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Plan<span className="required">*</span></label>
                                <select
                                    value={form.planId}
                                    name="planId"
                                    onChange={(e) => {
                                        const pId = e.target.value;
                                        const plan = plans.find(p => p._id === pId);
                                        setForm({
                                            ...form,
                                            planId: pId,
                                            amount: plan ? plan.price : form.amount
                                        });
                                    }}
                                    required
                                >
                                    <option value="">Select Plan</option>
                                    {plans.map(p => <option key={p._id} value={p._id}>{p.name} (${p.price})</option>)}
                                </select>
                            </div>

                            {form.planId && (
                                <div style={{
                                    border: '1px solid #3f51b522',
                                    background: '#f0f4ff',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '20px',
                                    marginTop: '-10px'
                                }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <div style={{ fontSize: '11.14px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>
                                                Price
                                            </div>
                                            <div style={{ fontSize: '17.83px', fontWeight: '700', color: '#3f51b5' }}>
                                                ${form.amount} <span style={{ fontSize: '12.25px', fontWeight: 'normal', color: '#64748b' }}>/ {plans.find(p => p._id === form.planId)?.pricePeriod}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '11.14px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>
                                                Expires On
                                            </div>
                                            <div style={{ fontSize: '15.60px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center' }}>
                                                <Icon d={icons.calendar} size={13} color="#3f51b5" style={{ marginRight: 6 }} />
                                                {form.nextBillingDate ? new Date(form.nextBillingDate).toLocaleDateString('en-GB') : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label>Payment Mode<span className="required">*</span></label>
                                <input
                                    type="text"
                                    placeholder="e.g. Offline, Credit Card, Bank Transfer"
                                    value={form.transactionType}
                                    onChange={(e) => setForm({ ...form, transactionType: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Amount<span className="required">*</span></label>
                                <input
                                    type="number"
                                    placeholder="Amount"
                                    value={form.amount}
                                    readOnly
                                    style={{ background: '#f8fafc' }}
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="submit" className="submit-btn" disabled={saving}>
                                    {saving ? 'Processing...' : (editId ? 'Update' : 'Submit')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .sub-redesign-container {
                    padding: 24px;
                    background-color: #f8fafc;
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }
                .sub-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding: 0 4px;
                }
                .sub-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                }
                .sub-btn-add {
                    background-color: #3f51b5;
                    color: white;
                    border: none;
                    padding: 10px 18px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    transition: background 0.2s;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .sub-btn-add:hover {
                    background-color: #303f9f;
                }
                .sub-card {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                }
                .sub-card-top {
                    padding: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid #f1f5f9;
                }
                .sub-per-page {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #64748b;
                    font-size: 14px;
                }
                .sub-per-page select {
                    padding: 6px 30px 6px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    background: white;
                    color: #475569;
                    font-size: 14px;
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='Wait-M19 9l-7 7-7-7'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 10px center;
                    background-size: 12px;
                }
                .sub-search {
                    position: relative;
                    width: 240px;
                }
                .sub-search input {
                    width: 100%;
                    padding: 8px 12px 8px 36px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s;
                }
                .sub-search input:focus {
                    border-color: #3f51b5;
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                .sub-table-new {
                    width: 100%;
                    border-collapse: collapse;
                }
                .sub-table-new th {
                    background-color: #edeffc;
                    padding: 16px 20px;
                    text-align: left;
                    font-size: 13px;
                    font-weight: 600;
                    color: #475569;
                    border-bottom: 1px solid #e2e8f0;
                }
                .sub-table-new td {
                    padding: 16px 20px;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .plan-cell {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .plan-icon {
                    width: 36px;
                    height: 36px;
                    background: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #e2e8f0;
                }
                .plan-name {
                    font-weight: 700;
                    color: #334155;
                    font-size: 14px;
                    margin-bottom: 2px;
                }
                .plan-sub {
                    font-size: 12px;
                    color: #64748b;
                }
                .date-cell div {
                    font-size: 13px;
                    color: #475569;
                }
                .time-sub {
                    color: #64748b !important;
                    margin-top: 2px;
                }
                .subscribed-on {
                    font-size: 11px !important;
                    color: #94a3b8 !important;
                    margin-top: 2px;
                }
                .billing-cell .price-label {
                    font-size: 14px;
                    color: #1e293b;
                    margin-bottom: 4px;
                }
                .billing-cell .time-remaining {
                    font-size: 12px;
                    color: #64748b;
                }
                .payment-method {
                    color: #64748b;
                    font-size: 14px;
                }
                .status-pill {
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    display: inline-block;
                }
                .status-pill.active { background: #dcfce7; color: #15803d; }
                .status-pill.inactive { background: #f1f5f9; color: #64748b; }
                .status-pill.rejected { background: #fee2e2; color: #dc2626; }
                
                .action-cell {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 12px;
                }
                .action-pill {
                    padding: 6px 16px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    border: none;
                    transition: opacity 0.2s;
                }
                .inactive-btn { background: #334155; color: white; }
                .active-btn { background: #22c55e; color: white; }
                .action-pill:hover { opacity: 0.9; }
                
                .more-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                }

                .sub-pagination {
                    padding: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-top: 1px solid #f1f5f9;
                }
                .pagination-info {
                    font-size: 13px;
                    color: #64748b;
                }
                .pagination-ctrls {
                    display: flex;
                    gap: 1px;
                }
                .page-btn {
                    padding: 8px 14px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    font-size: 13px;
                    color: #64748b;
                    cursor: pointer;
                }
                .page-btn.active {
                    background: #3f51b5;
                    color: white;
                    border-color: #3f51b5;
                }
                .page-btn:first-child { border-radius: 6px 0 0 6px; }
                .page-btn:last-child { border-radius: 0 6px 6px 0; }

                /* Modal Styles */
                .sub-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    backdrop-filter: blur(2px);
                }
                .sub-modal {
                    background: white;
                    width: 100%;
                    max-width: 450px;
                    border-radius: 8px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                    animation: fadeIn 0.2s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                }
                .close-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                }
                .modal-form {
                    padding: 24px;
                }
                .form-group {
                    margin-bottom: 20px;
                }
                .form-group label {
                    display: block;
                    font-size: 13px;
                    font-weight: 500;
                    color: #475569;
                    margin-bottom: 8px;
                }
                .required { color: #dc2626; margin-left: 2px; }
                .form-group select, .form-group input {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 14px;
                    outline: none;
                    color: #334155;
                }
                .form-group select:focus, .form-group input:focus {
                    border-color: #3f51b5;
                    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
                }
                .existing-plans-info {
                    font-size: 11px;
                    color: #3f51b5;
                    margin-top: 6px;
                    font-weight: 500;
                    padding: 4px 8px;
                    background: #f0f4ff;
                    border-radius: 4px;
                    border-left: 2px solid #3f51b5;
                }
                .modal-footer {
                    margin-top: 32px;
                    display: flex;
                    justify-content: flex-end;
                }
                .submit-btn {
                    background: #3f51b5;
                    color: white;
                    border: none;
                    padding: 10px 24px;
                    border-radius: 4px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .submit-btn:hover { background: #303f9f; }
                .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                .pp-toast {
                    position: fixed;
                    top: 24px;
                    right: 24px;
                    padding: 12px 24px;
                    border-radius: 8px;
                    color: white;
                    font-weight: 600;
                    z-index: 2000;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                    animation: slideIn 0.3s ease-out;
                }
                .pp-toast.success { background: #10b981; }
                .pp-toast.error { background: #ef4444; }
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }

                .action-dropdown {
                    position: absolute;
                    right: 0;
                    top: 100%;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    z-index: 100;
                    min-width: 120px;
                    padding: 6px 0;
                    margin-top: 6px;
                    animation: fadeIn 0.1s ease-out;
                }
                .action-dropdown button {
                    width: 100%;
                    text-align: left;
                    padding: 10px 16px;
                    background: none !important;
                    border: none !important;
                    font-size: 13px !important;
                    color: #475569 !important;
                    cursor: pointer;
                    transition: background 0.2s;
                    display: block !important;
                    font-weight: 500 !important;
                }
                .action-dropdown button:hover {
                    background: #f1f5f9 !important;
                    color: #1e293b !important;
                }
                .action-dropdown button.delete:hover {
                    background: #fee2e2 !important;
                }

                /* --- Dark Mode Overrides --- */
                .theme-dark .sub-redesign-container {
                    background-color: #0f0f0f;
                }
                .theme-dark .sub-title {
                    color: #fff;
                }
                .theme-dark .sub-card {
                    background: #161616;
                    border-color: #222;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
                }
                .theme-dark .sub-card-top {
                    border-bottom-color: #222;
                }
                .theme-dark .sub-per-page {
                    color: #888;
                }
                .theme-dark .sub-per-page select {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23555'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
                }
                .theme-dark .sub-search input {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .sub-table-new th {
                    background-color: #1e1e1e;
                    color: #888;
                    border-bottom-color: #2a2a2a;
                }
                .theme-dark .sub-table-new td {
                    border-bottom-color: #222;
                    color: #ccc;
                }
                .theme-dark .plan-icon {
                    background: #1a1a1a;
                    border-color: #2a2a2a;
                }
                .theme-dark .plan-name {
                    color: #fff;
                }
                .theme-dark .plan-sub {
                    color: #888;
                }
                .theme-dark .date-cell div {
                    color: #ccc;
                }
                .theme-dark .subscribed-on {
                    color: #555 !important;
                }
                .theme-dark .billing-cell .price-label {
                    color: #eee;
                }
                .theme-dark .billing-cell .time-remaining {
                    color: #888;
                }
                .theme-dark .payment-method {
                    color: #777;
                }
                .theme-dark .status-pill.active {
                    background: rgba(21, 128, 61, 0.2);
                    color: #22c55e;
                }
                .theme-dark .status-pill.inactive {
                    background: #2a2a2a;
                    color: #888;
                }
                .theme-dark .sub-pagination {
                    border-top-color: #222;
                }
                .theme-dark .page-btn {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .sub-modal {
                    background: #1a1a1a;
                    border: 1px solid #242424;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
                }
                .theme-dark .modal-header {
                    border-bottom-color: #242424;
                }
                .theme-dark .modal-header h3 {
                    color: #fff;
                }
                .theme-dark .form-group label {
                    color: #888;
                }
                .theme-dark .form-group select,
                .theme-dark .form-group input {
                    background: #111;
                    border-color: #2a2a2a;
                    color: #ddd;
                }
                .theme-dark .form-group select:focus,
                .theme-dark .form-group input:focus {
                    border-color: #3f51b5;
                }
                .theme-dark .existing-plans-info {
                    background: rgba(63, 81, 181, 0.1);
                    border-left-color: #3f51b5;
                    color: #3f51b5;
                }
                .theme-dark .action-dropdown {
                    background: #1a1a1a;
                    border-color: #2a2a2a;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
                }
                .theme-dark .action-dropdown button {
                    color: #aaa !important;
                }
                .theme-dark .action-dropdown button:hover {
                    background: #2a2a2a !important;
                    color: #fff !important;
                }
            `}</style>
        </div>
    );
}
